import React, { useMemo, useState, PropsWithChildren, useCallback } from 'react';
import isNil from 'lodash-es/isNil';

import { getSeriesSeasons, type Season, type Series } from '~/src/common/lean-back';

import { useContinueWatchingEpisode } from '../../useContinueWatchingEpisode';

type OnActiveSeasonChange = (seasonId: Season['id'] | null) => void;

type SeasonsContextValue = {
  seasonsList: Season[];
  activeSeason: Season | null;
  onActiveSeasonChange: OnActiveSeasonChange;
  loading: boolean;
};

interface Props {
  series: Series;
}

const SeasonsContext = React.createContext<SeasonsContextValue | undefined>(undefined);

function calculateDetaulsSeason({
  isFirstWatch,
  nextEpisode,
  seasonsList,
}: {
  isFirstWatch: boolean;
  seasonsList: Season[];
  nextEpisode: { seasonNum: number } | null;
}) {
  if (isFirstWatch) {
    const firstSeason = seasonsList[0] ?? null;
    return firstSeason;
  }

  const continueWatchingSeriesSeason =
    seasonsList.find(sItem => sItem.seasonNum === nextEpisode?.seasonNum) ?? null;
  return continueWatchingSeriesSeason;
}

export const SeasonsProvider: React.FC<PropsWithChildren<Props>> = ({ series, children }) => {
  const { isFirstWatch, nextEpisode, loading } = useContinueWatchingEpisode(series);

  const list = getSeriesSeasons(series);

  const [selectedActiveSeason, setSelectedActiveSeason] = useState<Season | null>(null);

  const onActiveSeasonChange = useCallback<OnActiveSeasonChange>(
    seasonId => {
      const selectedSeason = seasonId === null ? null : list.find(({ id }) => seasonId === id);
      setSelectedActiveSeason(selectedSeason ?? null);
    },
    [list],
  );

  const defaultActiveSeason = calculateDetaulsSeason({
    isFirstWatch,
    seasonsList: list,
    nextEpisode,
  });

  const value = useMemo(() => {
    const activeSeason = !isNil(selectedActiveSeason) ? selectedActiveSeason : defaultActiveSeason;
    return {
      activeSeason,
      onActiveSeasonChange,
      seasonsList: list,
      loading,
    };
  }, [selectedActiveSeason, defaultActiveSeason, onActiveSeasonChange, list, loading]);

  return <SeasonsContext.Provider value={value}>{children}</SeasonsContext.Provider>;
};

export function useSeasonsState() {
  const context = React.useContext(SeasonsContext);
  if (typeof context === 'undefined') {
    throw new Error('useSeasonsState must be used within a SeasonsContext');
  }
  return context;
}
